import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const menuSections = [
  {
    label: '홈',
    items: [
      { path: '/dashboard', icon: '📊', label: '내 대시보드', end: true },
    ],
  },
  {
    label: '대본',
    items: [
      { path: '/script', icon: '📄', label: '대본 생성', end: true },
      { path: '/podcast/setup', icon: '🎙️', label: '팟캐스트 대본' },
      { path: '/podcast/cast', icon: '👥', label: '출연진 관리' },
      { path: '/script/library', icon: '📑', label: '대본 보관함' },
    ],
  },
  {
    label: '영상 자동편집',
    items: [
      { path: '/v3/project/new', icon: '🎬', label: '새 프로젝트' },
      { path: '/v3/project', icon: '📂', label: '프로젝트 목록', end: true },
    ],
  },
  {
    label: '도구',
    items: [
      { path: '/video/editor', icon: '✂️', label: '영상 편집기' },
      { path: '/video/library', icon: '📦', label: '영상 보관함' },
    ],
  },
];

export default function Layout() {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const [tokens, setTokens] = useState(null);

  useEffect(() => {
    if (user) {
      api.get('/token/balance').then(r => setTokens(r.data.tokens)).catch(() => {});
    }
  }, [user, api]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div style={styles.wrapper}>
      <aside style={styles.sidebar}>
        {/* 로고 */}
        <div style={styles.logoArea}>
          <div style={styles.logoIcon}>A</div>
          <div>
            <div style={styles.logoText}>AI Tuner X</div>
            <div style={styles.logoSub}>AI Video Editor</div>
          </div>
        </div>

        {/* 메뉴 */}
        <nav style={styles.nav}>
          {menuSections.map((section, si) => (
            <div key={si} style={si > 0 ? { marginTop: 8 } : undefined}>
              <div style={styles.sectionLabel}>{section.label}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  style={({ isActive }) => ({
                    ...styles.menuItem,
                    ...(isActive ? styles.menuItemActive : {}),
                  })}
                >
                  <span style={styles.menuIcon}>{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}

          {user?.role === 'admin' && (
            <div style={{ marginTop: 8 }}>
              <div style={styles.sectionDivider} />
              <NavLink
                to="/admin"
                style={({ isActive }) => ({
                  ...styles.menuItem,
                  ...(isActive ? styles.menuItemActive : {}),
                })}
              >
                <span style={styles.menuIcon}>👑</span>
                <span>관리자</span>
              </NavLink>
            </div>
          )}
        </nav>

        {/* 사용자 정보 */}
        <div style={styles.userArea}>
          {tokens !== null && (
            <div style={{
              padding: '10px 16px',
              background: 'rgba(0,229,255,0.08)',
              borderRadius: 8,
              border: '1px solid rgba(0,229,255,0.15)',
              marginBottom: 8,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/tokens')}
            >
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 2 }}>보유 토큰</div>
              {user?.role === 'admin' || user?.role === 'manager' ? (
                <>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#00F0FF' }}>♾️ 무제한</div>
                  <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>관리자 계정</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#00F0FF' }}>🪙 {tokens.toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>충전하기 →</div>
                </>
              )}
            </div>
          )}
          <div style={styles.userInfo}>
            <div style={styles.avatar}>
              {user?.username?.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.userName}>{user?.username}</div>
              <div style={styles.userRole}>
                {user?.role === 'admin' ? '관리자' : '사용자'}
              </div>
            </div>
          </div>
          <button onClick={handleLogout} style={styles.logoutBtn}>
            로그아웃
          </button>
        </div>
      </aside>

      <main style={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}

const styles = {
  wrapper: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#0D1117',
    fontFamily: "'Noto Sans KR', sans-serif",
  },
  sidebar: {
    width: 200,
    backgroundColor: '#161B22',
    borderRight: '1px solid #2A3441',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 100,
  },
  logoArea: {
    padding: '20px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    borderBottom: '1px solid #2A3441',
  },
  logoIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'linear-gradient(135deg, #4CAF50, #2E7D32)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    fontWeight: 800,
    flexShrink: 0,
  },
  logoText: {
    color: '#E6EDF3',
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  logoSub: {
    color: '#4CAF50',
    fontSize: 10,
    fontWeight: 500,
    letterSpacing: '0.5px',
  },
  nav: {
    flex: 1,
    padding: '8px 8px',
    overflowY: 'auto',
  },
  sectionLabel: {
    padding: '10px 12px 4px',
    fontSize: 10,
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  sectionDivider: {
    height: 1,
    backgroundColor: '#2A3441',
    margin: '4px 12px 8px',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 12px',
    borderRadius: 10,
    color: '#8B949E',
    textDecoration: 'none',
    fontSize: 13,
    fontWeight: 500,
    transition: 'all 0.15s',
    marginBottom: 2,
  },
  menuItemActive: {
    backgroundColor: 'rgba(76, 175, 80, 0.12)',
    color: '#4CAF50',
  },
  menuIcon: {
    fontSize: 16,
    width: 22,
    textAlign: 'center',
    flexShrink: 0,
  },
  userArea: {
    padding: 12,
    borderTop: '1px solid #2A3441',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #4CAF50, #2E7D32)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  userName: {
    color: '#E6EDF3',
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  userRole: {
    color: '#6B7280',
    fontSize: 11,
  },
  logoutBtn: {
    width: '100%',
    padding: '7px',
    backgroundColor: 'transparent',
    border: '1px solid #2A3441',
    borderRadius: 8,
    color: '#6B7280',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: "'Noto Sans KR', sans-serif",
    transition: 'all 0.15s',
  },
  main: {
    flex: 1,
    marginLeft: 200,
    padding: 32,
    color: '#E6EDF3',
    minHeight: '100vh',
  },
};
